import type { components } from '@/services/api-allo/api-allo-v1';
import type { Line, PhoneNumberData, Sms10DlcStatus } from '@/types/calls-types';
import type {
  VoiceContact,
  VoiceContactExtra,
  VoiceContactUserLine,
} from '@packages/client-sdk-react/src/voice/voice-call-container';
import { formatPhoneNumber } from '@packages/corekit/src/utils/phoneNumbers';
import type { CountryCode, PhoneNumber } from 'libphonenumber-js';
import { countries, handleUnitedKingdomException, parsePhoneNumber } from './countries';

// Priority countries to try first (most common)
const PRIORITY_COUNTRIES: CountryCode[] = [
  'US',
  'CA',
  'FR',
  'GB',
  'DE',
  'ES',
  'IT',
  'AU',
  'NL',
  'BE',
  'CH',
  'AT',
  'SE',
  'NO',
  'DK',
  'FI',
  'IE',
  'PT',
  'PL',
  'NZ',
];

const ALL_COUNTRY_CODES = Object.keys(countries) as CountryCode[];
const OTHER_COUNTRIES = ALL_COUNTRY_CODES.filter((code) => !PRIORITY_COUNTRIES.includes(code));

/**
 * Try to parse and validate a phone number by trying multiple countries.
 * Returns the validation result and parsed PhoneNumber if valid.
 */
export const validatePhoneNumber = (phoneNumber: string, defaultCountry?: CountryCode) => {
  const countriesToTry: (CountryCode | undefined)[] = [
    undefined,
    defaultCountry,
    ...PRIORITY_COUNTRIES.filter((c) => c !== defaultCountry),
    ...OTHER_COUNTRIES,
  ];

  for (const country of countriesToTry) {
    if (country === undefined && defaultCountry === undefined) {
      continue;
    }
    const parsed = parsePhoneNumber(phoneNumber, country);
    if (parsed?.isValid()) {
      return {
        isValid: true,
        number: parsed,
      };
    }
  }

  return {
    isValid: false,
    number: undefined,
  };
};

const SMS_SHORT_CODE_RANGES: Partial<Record<CountryCode, { min: number; max: number }>> = {
  US: { min: 5, max: 6 },
  CA: { min: 5, max: 6 },
  FR: { min: 4, max: 5 },
  GB: { min: 5, max: 6 },
  DE: { min: 4, max: 5 },
  AU: { min: 6, max: 8 },
  IN: { min: 5, max: 5 },
  ES: { min: 4, max: 6 },
  IT: { min: 5, max: 5 },
  NL: { min: 4, max: 4 },
  BE: { min: 4, max: 4 },
  CH: { min: 3, max: 5 },
  AT: { min: 4, max: 6 },
  SE: { min: 5, max: 5 },
  NO: { min: 5, max: 5 },
  DK: { min: 4, max: 4 },
  FI: { min: 5, max: 6 },
  IE: { min: 5, max: 5 },
  PT: { min: 5, max: 5 },
  PL: { min: 4, max: 5 },
};
const DEFAULT_SMS_SHORT_CODE_RANGE = { min: 3, max: 6 };

export const isSmsShortCode = (phoneNumber: string, countryCode?: CountryCode): boolean => {
  const clean = phoneNumber.trim().replace(/\s/g, '');
  if (!/^\d+$/.test(clean)) {
    return false;
  }
  const range = (countryCode && SMS_SHORT_CODE_RANGES[countryCode]) ?? DEFAULT_SMS_SHORT_CODE_RANGE;
  return clean.length >= range.min && clean.length <= range.max;
};

const isShortNumber = (phoneNumber: string, countryCode?: CountryCode): boolean => {
  const clean = phoneNumber.trim().replace(/\s/g, '');
  if (!/^\d+$/.test(clean)) {
    return false;
  }
  if (clean.length >= 2 && clean.length <= 3) {
    return true; // emergency
  }
  return isSmsShortCode(clean, countryCode);
};

export const parsePhoneNumberData = (
  phoneNumber: string,
  user?: { name: string },
  defaultCountry?: CountryCode | 'UK',
) => {
  const normalizedCountry = defaultCountry
    ? handleUnitedKingdomException(defaultCountry)
    : undefined;

  if (normalizedCountry && isShortNumber(phoneNumber, normalizedCountry)) {
    const cleanNumber = phoneNumber.trim().replace(/\s/g, '');
    return {
      number: {
        country: normalizedCountry,
        number: cleanNumber,
        format: (_format: string) => cleanNumber,
        formatNational: () => cleanNumber,
        formatInternational: () => cleanNumber,
        isValid: () => true,
      } as any,
      label: cleanNumber,
      user: user
        ? {
            name: user.name,
          }
        : undefined,
    } satisfies PhoneNumberData;
  }

  let parsedPhoneNumber = parsePhoneNumber(phoneNumber, normalizedCountry);

  if (
    !phoneNumber.startsWith('+') &&
    defaultCountry &&
    (!parsedPhoneNumber || !parsedPhoneNumber.isValid())
  ) {
    const parsedWithPlus = parsePhoneNumber(`+${phoneNumber}`);
    if (parsedWithPlus?.isValid()) {
      parsedPhoneNumber = parsedWithPlus;
    }
  }

  if (!parsedPhoneNumber || !parsedPhoneNumber.isValid()) {
    return undefined;
  }

  return {
    number: parsedPhoneNumber,
    label:
      parsedPhoneNumber.country === defaultCountry
        ? parsedPhoneNumber.formatNational()
        : parsedPhoneNumber.formatInternational(),
    user: user
      ? {
          name: user.name,
        }
      : undefined,
  } satisfies PhoneNumberData;
};

/**
 * Parses a line object from the API into a Line type.
 * Handles both regular phone lines and sender ID lines.
 */
export const parseLine = (line: {
  number?: string;
  name?: string;
  isShared?: boolean;
  isOutboundSMSEnabled: boolean;
  sms10DLCStatus: Sms10DlcStatus;
  isOutboundMMSEnabled: boolean;
  mode?: 'BUSY' | 'FREE';
  type?: 'REGULAR' | 'SENDER_ID';
  senderId?: string;
  canMakeCalls?: boolean;
  country?: CountryCode | string | null;
  rateGroup?: string;
  status?: 'ACTIVE' | 'UNASSIGNED' | 'PORTING';
  defaultSummaryTemplateId?: string | null;
}) => {
  if (line.type === 'SENDER_ID' && line.senderId) {
    const senderIdCountry = (line.country as CountryCode) ?? 'FR';
    return {
      lineName: line.name ?? line.senderId,
      phoneNumber: {
        number: {
          country: senderIdCountry,
          number: line.senderId,
          format: (_fmt?: string) => line.senderId ?? '',
          formatNational: () => line.senderId ?? '',
          formatInternational: () => line.senderId ?? '',
          isValid: () => true,
        } as unknown as PhoneNumber,
        label: line.senderId,
      },
      isShared: line.isShared,
      isOutboundSMSEnabled: line.isOutboundSMSEnabled,
      sms10DLCStatus: line.sms10DLCStatus,
      isOutboundMMSEnabled: line.isOutboundMMSEnabled,
      mode: line.mode,
      type: 'SENDER_ID' as const,
      senderId: line.senderId,
      canMakeCalls: false,
      rateGroup: line.rateGroup,
      defaultSummaryTemplateId: line.defaultSummaryTemplateId ?? null,
    } satisfies Line;
  }

  // Regular phone number handling
  if (!line.number) {
    return undefined;
  }

  const phoneNumber = parsePhoneNumber(line.number);

  if (!phoneNumber) {
    return undefined;
  }

  return {
    lineName: line.name ?? line.number,
    phoneNumber: {
      number: phoneNumber,
      label: phoneNumber.formatNational(),
    },
    isShared: line.isShared,
    isOutboundSMSEnabled: line.isOutboundSMSEnabled,
    sms10DLCStatus: line.sms10DLCStatus,
    isOutboundMMSEnabled: line.isOutboundMMSEnabled,
    mode: line.mode,
    type: 'REGULAR' as const,
    canMakeCalls: line.canMakeCalls ?? true,
    rateGroup: line.rateGroup,
    status: line.status,
    defaultSummaryTemplateId: line.defaultSummaryTemplateId ?? null,
  } satisfies Line;
};

export const parseContact = (contact: components['schemas']['Contact']) => {
  let label = contact.numbers?.[0];

  if (contact.name && contact.last_name) {
    label = `${contact.name} ${contact.last_name}`;
  } else if (contact.name) {
    label = contact.name;
  } else if (contact.last_name) {
    label = contact.last_name;
  }

  return {
    label,
  };
};

export const parseWhisper = (
  whisper: NonNullable<components['schemas']['WhisperResponse']['data']>,
  from: string,
): {
  contact: VoiceContact;
  extra: VoiceContactExtra;
  userLine?: VoiceContactUserLine;
} => {
  const contact = whisper.contact && parseContact(whisper.contact);

  if (!contact) {
    return {
      contact: {
        name: from,
        phoneNumber: from,
        extractedName: whisper.contact?.last_extracted_name,
        jobTitle: whisper.contact?.job_title ?? undefined,
        companyName: whisper.contact?.company?.name,
      },
      extra: {
        summary: whisper?.summary,
        external_data: whisper.contact?.external_data,
      },
    };
  }

  return {
    contact: {
      id: whisper.contact?.id,
      name: contact.label,
      phoneNumber: from,
      extractedName: whisper.contact?.last_extracted_name,
      jobTitle: whisper.contact?.job_title ?? undefined,
      companyName: whisper.contact?.company?.name,
    },
    extra: {
      summary: whisper?.summary,
      external_data: whisper.contact?.external_data,
    },
    userLine: {
      lineName: whisper.phone_name,
      lineNumber: whisper.phone_number,
      originalToNumber: whisper.original_to_number,
      originalToName: whisper.original_to_name,
    },
  };
};

export const splitPhoneNumberByPrefixAndNumber = (
  phoneNumber: string,
  prefix: string,
  countryCode: CountryCode | 'UK',
) => {
  if (!phoneNumber) {
    return {
      prefix: '',
      number: '',
    };
  }

  if (countryCode === 'US' || countryCode === 'CA') {
    return {
      prefix: phoneNumber.slice(0, 5),
      number: phoneNumber.replace(`(${prefix})`, ''),
    };
  }

  return {
    prefix: phoneNumber.slice(0, 2),
    number: phoneNumber.replace(prefix, ''),
  };
};

export const getCountryCodeFromPhoneNumber = (phoneNumber: string) => {
  return formatPhoneNumber(phoneNumber).country as CountryCode;
};

/**
 * Returns a unique identifier for a line.
 * For regular lines, returns the E.164 formatted phone number.
 * For sender ID lines, returns "sender-id:{senderId}".
 * Falls back to "line:{lineName}" if neither is available.
 */
export const getLineIdentifier = (line: Line): string => {
  // Check for sender ID lines first to avoid calling format on mock object
  if (line.type === 'SENDER_ID' && line.senderId) {
    return `sender-id:${line.senderId}`;
  }
  const e164 = line.phoneNumber?.number?.format?.('E.164');
  if (e164) {
    return e164;
  }
  if (line.senderId) {
    return `sender-id:${line.senderId}`;
  }
  return `line:${line.lineName}`;
};

export const formatPhoneNumberForDisplay = (
  phoneNumber: string | undefined,
  currentLineCountry: CountryCode | undefined,
): string => {
  if (!phoneNumber) {
    return '';
  }

  const { number } = formatPhoneNumber(phoneNumber, {
    countryCode: currentLineCountry,
    format: 'auto',
  });

  return number;
};

export const getNumberAndCountryCode = (phoneNumber: string) => {
  const { number, country } = formatPhoneNumber(phoneNumber, { format: 'national' });

  return {
    number,
    countryCode: country,
  };
};

// Loose check: does this string look like a phone number rather than a person's
// name? Used to skip initials rendering when the "name" we received from the
// API is actually `+17272141460` or `(415) 555-1234`.
export const isPhoneLike = (value: string): boolean => /^\+?[\d\s().-]+$/.test(value.trim());
